/**
 * Form 4180 (Excise-Tax workflow, 03-17) field metadata: the flat interview blob
 * keys (snake_case, matching `apps/forms/mappings/form_4180.py`), the empty
 * baseline, an initial-values normalizer, and a light UX validation schema. The
 * four line-item lists (individuals/banks/eftps/positions) are child tables — not
 * part of this blob (see `./api.ts`).
 */

import * as Yup from "yup";

/** The blob is a mix of choice/text strings and checkbox booleans. */
export type Form4180Data = Record<string, string | boolean>;

/** Section II duty questions (a–g): each a yes/no + see-explanation gate. */
export const SEC2_DUTIES: { key: string; label: string }[] = [
  { key: "finc_policy_busns", label: "a. Determine financial policy for the business?" },
  { key: "auth_pay_bill", label: "b. Direct or authorize payments of bills/creditors?" },
  { key: "auth_trans_payroll", label: "c. Prepare, review, sign, or authorize transmit of payroll tax returns?" },
  { key: "know_wh_tax", label: "d. Have knowledge that withheld taxes were not paid?" },
  { key: "auth_payroll", label: "e. Authorize payroll?" },
  { key: "fed_tax_depo", label: "f. Authorize or make Federal Tax Deposits?" },
  { key: "auth_asgn_elect_bank", label: "g. Authorize the assignment of any EFTPS/electronic-banking PINs/passwords?" },
];

/** Checkbox (boolean) keys — everything else defaults to an empty string. */
export const BOOL_KEYS = new Set<string>([
  "notice_609",
  "notice_784",
  ...SEC2_DUTIES.map((d) => `${d.key}_chk`),
]);

const STRING_KEYS: string[] = [
  // Section 1
  "for_who", "business_name", "ein_no", "job_title",
  // Section 2 a–g (radio + from/to/explanation)
  ...SEC2_DUTIES.flatMap((d) => [d.key, `${d.key}_from`, `${d.key}_to`, `${d.key}_exp`]),
  "other_indvidual", "assign_bank",
  // Section 3
  "person_interviewed_date", "person_interviewer_date", "completed_interview_date",
  "taxpayer_stmt", "intr_cont_sub_page",
  // Section 4
  "use_eftps", "assgn_eftps_pwd", "oth_eftps", "where_oth_eftps_pwd",
  "assgn_oth_eftps_pwd", "fill_form_941", "auth_sign_941", "file_ret_941",
  // Section 5
  "oblg_bsn_paid", "oblg_bsn_paid_which", "oblg_bsn_paid_who",
  "payroll_port_met", "payroll_port_met_auth", "org_provd_fund", "org_provd_fund_exp",
  "aware_unpaid_tax", "action_attemp", "desc_nonpay_tax", "desc_nonpay_tax_identify",
  "handle_irs_contact", "handle_irs_contact_result",
  // Section 6a
  "third_party_payer", "psp_sign_service_cont", "psp_busn_handle", "psp_contact",
  "psp_fund_avail", "psp_bank_name_acc", "psp_verify_third_party",
  "psp_fund_avail_pay", "psp_fund_avail_pay_exp", "psp_not_make_req_pay", "psp_recv_irs_notice",
  // Section 6b
  "third_party_payer_comp", "tp_handle_contact", "tp_client_busn", "tp_agrm_for_service",
  "tp_ctrl_emp_tax", "tp_fund_mad_avail", "tp_bnk_name", "tp_accn_no",
  "tp_make_pay_tax", "tp_make_pay_tax_exp", "tp_make_pay_tax_action",
  // Section 7
  "resp_excise_tax", "et_ret_not_fill", "et_inc_sale_price", "et_tax_collect", "et_law_req",
  // Section 8
  "add_info",
];

export const EMPTY_4180: Form4180Data = {
  ...Object.fromEntries(STRING_KEYS.map((k) => [k, ""])),
  ...Object.fromEntries([...BOOL_KEYS].map((k) => [k, false])),
  for_who: "T",
};

/** Merge a stored `data` blob onto the empty baseline, coercing checkboxes. */
export function toInitialValues4180(data: Record<string, unknown>): Form4180Data {
  const out: Form4180Data = { ...EMPTY_4180 };
  Object.keys(out).forEach((k) => {
    const v = data[k];
    if (BOOL_KEYS.has(k)) {
      out[k] = Boolean(v);
    } else if (v !== undefined && v !== null) {
      out[k] = String(v);
    }
  });
  return out;
}

/**
 * Client-side (UX) validation — the server re-validates enum/length/date in the
 * mapper, so these are advisory only (conditional-required rules are deliberately
 * omitted so incremental autosave of a partly-filled blob is never blocked).
 */
export const schema4180 = Yup.object({
  ein_no: Yup.string().matches(/^\d*$/, "Digits only.").max(9, "Up to 9 digits."),
  job_title: Yup.string().max(100, "Up to 100 characters."),
});
